import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "@/lib/notify";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type ParticipantRow = { conversation_id: string };
type ChatMessageRow = { id: string; sender_id: string; content: string; conversation_id: string };
type NameRow = { user_id: string | null; nickname?: string | null; full_name?: string | null };



const getFirstName = (value?: string | null) => {
  const normalized = value?.trim();
  if (!normalized) return null;

  return normalized.split(/\s+/)[0] ?? normalized;
};

export function ChatMessageNotifier() {
  const { user } = useAuth();
  const location = useLocation();
  const [conversationIds, setConversationIds] = useState<Set<string>>(new Set());
  const [ownParticipantIds, setOwnParticipantIds] = useState<string[]>([]);
  const knownMessageIds = useRef<Set<string>>(new Set());
  const senderNameCache = useRef<Record<string, string>>({});

  const isChatRoute = useMemo(() => location.pathname.startsWith("/chat"), [location.pathname]);

  useEffect(() => {
    senderNameCache.current = {};
    knownMessageIds.current = new Set();
    setConversationIds(new Set());
    setOwnParticipantIds([]);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const loadOwnParticipantIds = async () => {
      const { data } = await (supabase as any)
        .from("profiles")
        .select("id")
        .eq("user_id", user.id);

      const profileIds = ((data as { id: string }[] | null) ?? []).map((row) => row.id);
      const ids = Array.from(new Set([user.id, ...profileIds]));
      setOwnParticipantIds(ids);
      return ids;
    };

    const loadConversationIds = async () => {
      const participantIds = ownParticipantIds.length ? ownParticipantIds : await loadOwnParticipantIds();
      const { data, error } = await (supabase as any)
        .from("chat_participants")
        .select("conversation_id")
        .in("user_id", participantIds);

      if (error) {
        console.error("[ChatMessageNotifier] Falha ao carregar conversas", error);
        return;
      }

      const nextIds = new Set(((data as ParticipantRow[] | null) ?? []).map((row) => row.conversation_id));
      setConversationIds(nextIds);
    };

    loadOwnParticipantIds().then(() => loadConversationIds());

    const participantsChannel = supabase
      .channel(`chat-notifier-participants-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_participants" },
        () => loadConversationIds(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(participantsChannel);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || conversationIds.size === 0) return;

    const getSenderDisplayName = async (senderId: string) => {
      const cached = senderNameCache.current[senderId];
      if (cached) return cached;

      const [profilesResult, professionalsResult] = await Promise.all([
        (supabase as any).from("profiles").select("user_id, nickname, full_name").or(`user_id.eq.${senderId},id.eq.${senderId}`).maybeSingle(),
        (supabase as any).from("v_professionals").select("user_id, nickname, full_name").eq("user_id", senderId).maybeSingle(),
      ]);

      const sources = [profilesResult.data, professionalsResult.data].filter(Boolean) as NameRow[];
      const resolvedName = sources
        .map((row) => row.nickname?.trim() || getFirstName(row.full_name) || null)
        .find((name): name is string => Boolean(name)) || "Usuário";

      senderNameCache.current[senderId] = resolvedName;
      return resolvedName;
    };

    const handleIncomingMessage = async (payload: { new: ChatMessageRow }) => {
      const message = payload.new;
      if (!message || message.sender_id === user.id || isChatRoute) return;
      if (!conversationIds.has(message.conversation_id)) return;
      if (knownMessageIds.current.has(message.id)) return;

      knownMessageIds.current.add(message.id);

      const senderName = await getSenderDisplayName(message.sender_id);
      const toastId = `chat-message-${message.id}`;

      toast(
        <button
          type="button"
          className="w-full text-left"
          onClick={() => toast.dismiss(toastId)}
        >
          {senderName}: {message.content}
        </button>,
        {
          id: toastId,
          duration: 8000,
          position: "bottom-right",
        },
      );
    };

    const messagesChannel = supabase
      .channel(`chat-notifier-messages-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, (payload) => {
        void handleIncomingMessage(payload as unknown as { new: ChatMessageRow });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
    };
  }, [conversationIds, isChatRoute, user?.id]);

  return null;
}
